import { computed, update } from "../core/signals/primitives.js";
import { defineBehavior } from "../core/composition/define-behavior.js";
import { createMachineReactor } from "../core/reactors/create-machine-reactor.js";
import { isResolvedPresentation } from "../media/types/index.js";
//#region src/playback/behaviors/setup-failover-monitor.ts
/**
* **CDN failover cooldown.** The expiry half of multi-CDN failover. Fetch sites own the _trip_: on a failed fetch they
* add the failing CDN (origin) to the `failedCdns` state signal directly. This behavior owns the _expiry_: while a
* presentation is resolved, it watches `failedCdns` and, for each CDN that appears, schedules a timer to remove it once
* its cooldown lapses. `track-switching`'s `excludeFailedCdns` constraint prunes a failed CDN's tracks and the
* active-CDN scope falls to the next one — and back, once the cooldown removes it here.
*
* Lifecycle is per-source: timers + `failedCdns` are cleared on exit (a new source starts with a clean slate). Policy
* (cooldown) is engine config. This is the minimal `network-resilience` slice — a single failure trips a CDN, since
* transient blips are the retry layer's job (it sits below the fetch sites, so anything that reaches `failedCdns` is
* already terminal).
*/
const DEFAULT_FAILOVER_MONITOR_CONFIG = { cooldownMs: 3e5 };
/**
* Expire failed CDNs from `failedCdns` once their cooldown lapses, for the resolved source.
*
* @example
*   const reactor = setupFailoverMonitor.setup({ state });
*/
const setupFailoverMonitor = defineBehavior({
	stateKeys: ["presentation", "failedCdns"],
	contextKeys: [],
	setup: ({ state, config = {} }) => {
		const cooldownMs = config.failover?.cooldownMs ?? DEFAULT_FAILOVER_MONITOR_CONFIG.cooldownMs;
		const timers = /* @__PURE__ */ new Map();
		const derivedStateSignal = computed(() => isResolvedPresentation(state.presentation.get()) ? "presentation-resolved" : "presentation-unresolved");
		return createMachineReactor({
			initial: "presentation-unresolved",
			monitor: () => derivedStateSignal.get(),
			states: {
				"presentation-unresolved": {},
				"presentation-resolved": {
					entry: () => () => {
						timers.forEach((timer) => clearTimeout(timer));
						timers.clear();
						state.failedCdns.set(void 0);
					},
					effects: [() => {
						(state.failedCdns.get() ?? []).forEach((cdn) => {
							if (timers.has(cdn)) return;
							const timer = setTimeout(() => {
								timers.delete(cdn);
								update(state.failedCdns, (current) => current?.filter((c) => c !== cdn));
							}, cooldownMs);
							timers.set(cdn, timer);
						});
					}]
				}
			}
		});
	}
});
//#endregion
export { setupFailoverMonitor };
